/** @file resampleImage: an image resized to any size, nearest-neighbour (pixel art) or area-averaged. */
import type { Image } from '../types/image';
import { createImage } from './createImage';

/** The source cells one output cell covers on an axis, each with the length it overlaps (sums to `scale`). */
function spans(outLen: number, srcLen: number): { from: number; weights: number[] }[] {
  const scale = srcLen / outLen;
  return Array.from({ length: outLen }, (_, o) => {
    const a = o * scale;
    const b = Math.min(srcLen, (o + 1) * scale);
    const from = Math.floor(a);
    const weights: number[] = [];
    for (let s = from; s < b; s++) weights.push(Math.min(b, s + 1) - Math.max(a, s));
    return { from, weights };
  });
}

/** Writes the premultiplied-alpha average of the cells `xs` x `ys` of `src` into byte `d` of `out`. */
function average(src: Image, out: Image, d: number, xs: { from: number; weights: number[] }, ys: { from: number; weights: number[] }): void {
  let r = 0; let g = 0; let b = 0; let a = 0; let total = 0;
  ys.weights.forEach((wy, j) => {
    xs.weights.forEach((wx, i) => {
      const s = ((ys.from + j) * src.width + xs.from + i) * 4;
      const w = wx * wy;
      const al = (src.data[s + 3] ?? 0) * w;
      r += (src.data[s] ?? 0) * al; g += (src.data[s + 1] ?? 0) * al; b += (src.data[s + 2] ?? 0) * al;
      a += al; total += w;
    });
  });
  if (a > 0) out.data.set([r / a, g / a, b / a, a / total], d);
}

/** Fills `out` with the source pixel under each output pixel's centre. */
function nearestInto(src: Image, out: Image): void {
  for (let y = 0; y < out.height; y++) {
    const sy = Math.min(src.height - 1, Math.floor(((y + 0.5) * src.height) / out.height));
    for (let x = 0; x < out.width; x++) {
      const s = (sy * src.width + Math.min(src.width - 1, Math.floor(((x + 0.5) * src.width) / out.width))) * 4;
      out.data.set(src.data.subarray(s, s + 4), (y * out.width + x) * 4);
    }
  }
}

/**
 * `src` resized to `width` x `height`. `nearest`: each output pixel takes the source pixel under its centre (crisp
 * pixel art). `linear`: each output pixel is the area-weighted average of the source pixels it covers, colours
 * weighted by alpha (no dark fringes), so a large painting shrinks smoothly. An empty size gives an empty image.
 */
export function resampleImage(src: Image, width: number, height: number, mode: 'nearest' | 'linear'): Image {
  const out = createImage(width, height);
  if (width <= 0 || height <= 0 || src.width === 0 || src.height === 0) return out;
  if (mode === 'linear') {
    const xs = spans(width, src.width);
    const ys = spans(height, src.height);
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) average(src, out, (y * width + x) * 4, xs[x] ?? { from: 0, weights: [] }, ys[y] ?? { from: 0, weights: [] });
    return out;
  }
  nearestInto(src, out);
  return out;
}
